'use client';

import { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import {
  configureKeyboard,
  hideSplash,
  isNative,
  isTouch,
  nativePlatform,
  onAppResume,
  onAppUrlOpen,
  onBackButton,
  onKeyboard,
  setStatusBarStyle,
} from '@/lib/native';
import { APP_RESUME_EVENT } from '@/lib/native/events';
import { deepLinkPath } from '@/lib/safe-return-to';

/**
 * Wires the native shell to the document. Mounted once in the root layout.
 *
 * - `html[data-native="ios"]` switches on the in-app CSS (globals.css).
 * - `html[data-touch]` marks hover-less devices for press feedback.
 * - Status bar matches the light theme; splash hides after first paint.
 * - Keyboard height → `--keyboard-height` + `data-keyboard="open"`.
 * - Android back button walks history; deep links route in-app.
 * - Foregrounding dispatches `sweetspot:app-resume` so feeds can refetch.
 */
export function NativeProvider() {
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const root = document.documentElement;
    if (isTouch()) root.setAttribute('data-touch', '');
    if (!isNative()) return;

    root.setAttribute('data-native', nativePlatform());
    void setStatusBarStyle('light');
    void configureKeyboard();
    // Two frames: let the first route paint under the splash before it fades.
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => void hideSplash()));

    const offKeyboard = onKeyboard((open, height) => {
      root.style.setProperty('--keyboard-height', `${open ? height : 0}px`);
      if (open) root.setAttribute('data-keyboard', 'open');
      else root.removeAttribute('data-keyboard');
    });
    const offResume = onAppResume(() => {
      window.dispatchEvent(new CustomEvent(APP_RESUME_EVENT));
    });
    const offBack = onBackButton((canGoBack) => {
      if (canGoBack) window.history.back();
    });
    const offUrl = onAppUrlOpen((url) => {
      const path = deepLinkPath(url);
      if (path) router.push(path);
    });

    return () => {
      cancelAnimationFrame(raf);
      offKeyboard();
      offResume();
      offBack();
      offUrl();
    };
  }, [router]);

  // Blur any focused input on route change so the keyboard never lingers over
  // the next screen (a classic web-wrapper tell).
  useEffect(() => {
    if (!isNative()) return;
    const el = document.activeElement as HTMLElement | null;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) el.blur();
  }, [pathname]);

  return null;
}
